import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { PriceSimulationStep } from '../types/PriceSimulationSteps';
import { savePriceSimulationState } from '../utils/priceSimulationLocalStorage';

interface PriceSimulationContextType {
  priceSimulationStep: PriceSimulationStep | null;
  setPriceSimulationStep: (priceSimulationStep: PriceSimulationStep) => void;
  derivedFormValues: PriceSimulationFormSchema | null;
}

export const PriceSimulationContext = createContext<PriceSimulationContextType>({
  priceSimulationStep: null,
  setPriceSimulationStep: () => {},
  derivedFormValues: null,
});

export const PriceSimulationProvider = ({ children }: PropsWithChildren) => {
  const { watch, getValues } = useFormContext<PriceSimulationFormSchema>();

  const [priceSimulationStep, setPriceSimulationStep] = useState<PriceSimulationStep | null>(null);

  // Save state to cookie whenever form values change
  const handleFormChange = useCallback(
    (formValues: PriceSimulationFormSchema) => {
      savePriceSimulationState({
        formValues,
        priceSimulationStep,
      });
    },
    [priceSimulationStep],
  );

  // Explicitly save on priceSimulationStep & formvalue changes
  useEffect(() => {
    const formValues = getValues();
    handleFormChange(formValues);
  }, [priceSimulationStep, handleFormChange, getValues]);

  const value = useMemo<PriceSimulationContextType>(
    () => ({
      priceSimulationStep,
      setPriceSimulationStep,
      derivedFormValues: watch(),
    }),
    [priceSimulationStep, watch],
  );

  return <PriceSimulationContext.Provider value={value}>{children}</PriceSimulationContext.Provider>;
};
